import * as XLSX from 'xlsx'

/**
 * Helpers para gerar e baixar planilhas Excel direto no cliente.
 *
 * Convenção:
 *   - Cabeçalho na primeira linha (auto-extraído ou definido).
 *   - Larguras de colunas auto-calculadas pelo maior conteúdo.
 *   - Arquivo final em .xlsx (BOM/encoding tratados pela lib).
 */

export interface ExportColumn<T> {
  /** Cabeçalho exibido na primeira linha. */
  header: string
  /** Função que extrai o valor da linha. Strings/numbers preferidos. */
  accessor: (row: T) => string | number | boolean | null | undefined
}

/**
 * Exporta um array de objetos como `.xlsx` e dispara o download.
 *
 *   exportToXlsx('clientes', rows, [
 *     { header: 'Nome', accessor: r => r.name },
 *     { header: 'CPF',  accessor: r => r.cpf },
 *   ])
 */
export function exportToXlsx<T>(
  filenameBase: string,
  rows: T[],
  columns: ExportColumn<T>[],
  sheetName: string = 'Dados',
): void {
  const header = columns.map((c) => c.header)
  const body = rows.map((r) => columns.map((c) => normalize(c.accessor(r))))

  const ws = XLSX.utils.aoa_to_sheet([header, ...body])

  // Auto-largura das colunas (limita a 60 chars pra evitar planilha gigante).
  ws['!cols'] = columns.map((_c, colIdx) => {
    const max = Math.max(
      header[colIdx].length,
      ...body.map((row) => String(row[colIdx] ?? '').length),
    )
    return { wch: Math.min(60, Math.max(8, max + 2)) }
  })

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, sheetName)

  const stamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-')
  const filename = `${filenameBase}-${stamp}.xlsx`
  XLSX.writeFile(wb, filename)
}

/**
 * Exporta múltiplas abas (uma por grupo) em um único `.xlsx`.
 *
 *   exportSheetsToXlsx('funcionarios', [
 *     { name: 'Matriz', rows, columns },
 *     { name: 'Filial X', rows, columns },
 *   ])
 */
export function exportSheetsToXlsx<T>(
  filenameBase: string,
  sheets: Array<{ name: string; rows: T[]; columns: ExportColumn<T>[] }>,
): void {
  const wb = XLSX.utils.book_new()
  const usedNames = new Set<string>()

  for (const sheet of sheets) {
    const header = sheet.columns.map((c) => c.header)
    const body = sheet.rows.map((r) => sheet.columns.map((c) => normalize(c.accessor(r))))
    const ws = XLSX.utils.aoa_to_sheet([header, ...body])
    ws['!cols'] = sheet.columns.map((_c, colIdx) => {
      const max = Math.max(header[colIdx].length, ...body.map((row) => String(row[colIdx] ?? '').length))
      return { wch: Math.min(60, Math.max(8, max + 2)) }
    })
    // Nome de aba: Excel limita a 31 chars e proíbe : \ / ? * [ ]; garante unicidade.
    let name = (sheet.name || 'Aba').replace(/[:\\/?*[\]]/g, ' ').slice(0, 28).trim() || 'Aba'
    let n = 2
    const baseName = name
    while (usedNames.has(name.toLowerCase())) { name = `${baseName.slice(0, 25)} ${n++}` }
    usedNames.add(name.toLowerCase())
    XLSX.utils.book_append_sheet(wb, ws, name)
  }

  const stamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-')
  XLSX.writeFile(wb, `${filenameBase}-${stamp}.xlsx`)
}

function normalize(v: unknown): string | number | boolean {
  if (v === null || v === undefined) return ''
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return v
  if (v instanceof Date) return v.toISOString()
  try { return JSON.stringify(v) } catch { return String(v) }
}
